import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../hooks/useTokens';
import { copyToClipboard } from '../utils/clipboard';
import { Link } from 'react-router-dom';

const WIDGETS = [
  { path: 'overall-performance', name: 'Overall Performance', description: 'KPI-oversikt med budsjett og faktureringsgrad' },
  { path: 'overall-performance/yearly-chart', name: 'Yearly Chart', description: 'Budsjett vs. faktisk per maned' },
  { path: 'team-kpis', name: 'Team KPIs', description: 'KPI-tabell per team' },
  { path: 'team-members', name: 'Team Members', description: 'Ansattoversikt med faktureringsgrad' },
  { path: 'resource-planning', name: 'Resource Planning', description: 'Ressursplanlegging 4 uker frem' },
  { path: 'announcements', name: 'Announcements', description: 'Kunngjoeringer fra Broennoey' },
];

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { tokens, loading, error, createToken, deleteToken } = useTokens(user?.email);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);

  const baseUrl = window.location.origin;

  const handleCreate = async () => {
    if (!newLabel.trim() || !user) return;
    setCreating(true);
    await createToken(newLabel.trim(), user.name);
    setNewLabel('');
    setCreating(false);
  };

  const handleCopy = async (text: string, id: string) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const activeToken = selectedToken || (tokens.length > 0 ? tokens[0].token : null);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Innstillinger</h1>
          <p className="text-sm text-slate-500 mt-1">Administrer embed-tokens og widget-integrasjoner</p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Tilbake til dashboard
        </Link>
      </div>

      {/* Token management */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Embed-tokens</h2>
        <p className="text-sm text-slate-500 mb-4">
          Opprett tokens for a bygge inn dashboard-widgets pa eksterne sider. Hver token kan brukes i embed-URLer.
        </p>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg mb-4">{error}</div>
        )}

        {/* Create new token */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="Beskrivelse (f.eks. TV-skjerm kontoret)"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newLabel.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? 'Oppretter...' : 'Opprett token'}
          </button>
        </div>

        {/* Token list */}
        {loading ? (
          <p className="text-sm text-slate-500">Laster tokens...</p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen tokens opprettet enna.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 font-medium text-slate-600">Beskrivelse</th>
                  <th className="text-left py-2 pr-4 font-medium text-slate-600">Token</th>
                  <th className="text-left py-2 pr-4 font-medium text-slate-600">Opprettet</th>
                  <th className="text-left py-2 pr-4 font-medium text-slate-600">Sist brukt</th>
                  <th className="text-right py-2 font-medium text-slate-600">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(t => (
                  <tr key={t.token} className="border-b border-slate-100">
                    <td className="py-3 pr-4 text-slate-800">{t.label || '-'}</td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono truncate max-w-[200px]">
                          {t.token}
                        </code>
                        <button
                          onClick={() => handleCopy(t.token, `token-${t.token}`)}
                          className="text-slate-400 hover:text-slate-600 shrink-0"
                          title="Kopier token"
                        >
                          {copiedId === `token-${t.token}` ? (
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-500">
                      {new Date(t.createdAt).toLocaleDateString('nb-NO')}
                    </td>
                    <td className="py-3 pr-4 text-slate-500">
                      {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString('nb-NO') : '-'}
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedToken(t.token === selectedToken ? null : t.token)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            t.token === selectedToken
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          Bruk i URLer
                        </button>
                        <button
                          onClick={() => deleteToken(t.token)}
                          className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        >
                          Slett
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Widget documentation */}
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Tilgjengelige widgets</h2>
        <p className="text-sm text-slate-500 mb-6">
          {activeToken
            ? 'Kopier URLer eller iframe-kode for a bygge inn widgets. Filtrer med query-parametere: team, horizontalTeam, month, employee, refresh (sekunder), size (small/medium/large).'
            : 'Opprett en token for a se embed-URLer og iframe-kode.'}
        </p>

        <div className="space-y-4">
          {WIDGETS.map(w => {
            const embedUrl = activeToken
              ? `${baseUrl}/embed/${w.path}?token=${activeToken}`
              : null;
            const iframe = embedUrl
              ? `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0"></iframe>`
              : null;

            return (
              <div key={w.path} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium text-slate-800">{w.name}</h3>
                    <p className="text-sm text-slate-500">{w.description}</p>
                    <p className="text-xs text-slate-400 mt-1 font-mono">/embed/{w.path}</p>
                  </div>
                </div>

                {embedUrl && (
                  <div className="mt-3 space-y-2">
                    {/* URL */}
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-slate-50 px-3 py-2 rounded border border-slate-200 font-mono truncate">
                        {embedUrl}
                      </code>
                      <button
                        onClick={() => handleCopy(embedUrl, `url-${w.path}`)}
                        className="shrink-0 text-xs px-3 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        {copiedId === `url-${w.path}` ? 'Kopiert!' : 'Kopier URL'}
                      </button>
                    </div>

                    {/* iframe */}
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-slate-50 px-3 py-2 rounded border border-slate-200 font-mono truncate">
                        {iframe}
                      </code>
                      <button
                        onClick={() => handleCopy(iframe!, `iframe-${w.path}`)}
                        className="shrink-0 text-xs px-3 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        {copiedId === `iframe-${w.path}` ? 'Kopiert!' : 'Kopier iframe'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default SettingsPage;
